import { type Item, UnitCode, type VendorDoc, VendorDocCreate, type VendorDocDraft, type VendorDocLineCreate, type VendorDocSummary, VendorDocType } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { type PickOption, Sheet, type SheetColumn } from '../components/Sheet';
import { byNumber, byText, useSort } from '../components/useSort';
import { Empty, ErrorBox, PageHead, QueryView } from '../components/ui';
import { formatDate, formatMoney, formatQty, packLabel, todayIso } from '../format';
import { useDesktop } from '../hooks';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

export type VendorDocKind = 'bills' | 'lists';
type DocSort = 'date' | 'vendor' | 'number' | 'lines' | 'unmapped' | 'total' | 'open' | 'due';

/** Where a document belongs in the sidebar: price lists apart, everything a vendor bills or credits us with as bills. */
export const kindOf = (docType: VendorDocType): VendorDocKind => (docType === 'price_list' ? 'lists' : 'bills');
export const kindPath = (kind: VendorDocKind | undefined) => (kind === 'lists' ? '/costs/price-lists' : kind === 'bills' ? '/costs/bills' : '/costs');

/** Which sidebar entry a /costs address belongs to: the lists by path, the form by its type, a document by ?k=. */
export function kindAt(pathname: string, search: string): VendorDocKind | null {
  if (pathname === '/costs/bills') return 'bills';
  if (pathname === '/costs/price-lists') return 'lists';
  if (!pathname.startsWith('/costs/')) return null;
  const params = new URLSearchParams(search);
  if (pathname === '/costs/new') return params.get('type') === 'price_list' ? 'lists' : params.get('type') ? 'bills' : null;
  const k = params.get('k');
  return k === 'bills' || k === 'lists' ? k : null;
}

/**
 * Vendor bills (invoices, credit memos, statements) or vendor price lists, newest first; without a kind, every
 * document. The desk reads them as a sortable table, the phone as a list.
 */
export function VendorDocsPage({ kind }: { kind?: VendorDocKind }) {
  const { t, intlLocale } = useLocale();
  const desktop = useDesktop();
  const navigate = useNavigate();
  const docs = useQuery({ queryKey: qk.vendorDocs(kind ?? 'all'), queryFn: ({ signal }) => api.vendorDocs(kind ? { kind } : {}, signal) });
  const title = kind === 'bills' ? t('vendorBills') : kind === 'lists' ? t('vendorPriceLists') : t('vendorDocs');
  const hint = kind === 'bills' ? t('vendorBillsHint') : kind === 'lists' ? t('vendorPriceListsHint') : t('vendorDocsHint');
  const enter = kind === 'bills' ? t('enterVendorBill') : kind === 'lists' ? t('enterVendorPriceList') : t('enterVendorDoc');
  const newPath = kind === 'lists' ? '/costs/new?type=price_list' : kind === 'bills' ? '/costs/new?type=invoice' : '/costs/new';
  const money = (v: number | null) => (v === null ? '' : formatMoney(v, intlLocale));
  // a paper read into history is never owed (decision 146): no open balance
  const open = (d: VendorDocSummary) => (d.total === null || d.docType !== 'invoice' || d.status === 'history' || d.status === 'void' ? null : Math.round((d.total - d.paid) * 100) / 100);
  const sorters = useMemo(
    () => ({
      date: byText<VendorDocSummary>((d) => d.docDate),
      vendor: byText<VendorDocSummary>((d) => d.vendorName),
      number: byText<VendorDocSummary>((d) => d.docNumber),
      lines: byNumber<VendorDocSummary>((d) => d.lineCount),
      unmapped: byNumber<VendorDocSummary>((d) => d.unmapped),
      total: byNumber<VendorDocSummary>((d) => d.total),
      open: byNumber<VendorDocSummary>((d) => open(d)),
      due: byText<VendorDocSummary>((d) => d.dueDate),
    }),
    [],
  );
  const { sorted, head } = useSort<VendorDocSummary, DocSort>(sorters, { key: 'date', dir: -1 }, ['date', 'lines', 'unmapped', 'total', 'open']);
  const columns: SheetColumn<VendorDocSummary>[] = [
    { key: 'date', header: head('date', t('dateCol')), label: t('dateCol'), width: '100px', render: (d) => formatDate(d.docDate, intlLocale, 'date') },
    { key: 'vendor', header: head('vendor', t('vendor')), label: t('vendor'), width: '150px', render: (d) => <Link to={`/costs/${d.id}?k=${kindOf(d.docType)}`}>{d.vendorName}</Link> },
    ...(kind === 'lists' ? [] : [{ key: 'type', header: t('docType'), width: '120px', render: (d: VendorDocSummary) => (d.status === 'history' ? `${t(`doc_${d.docType}`)} · ${t('docHistory')}` : t(`doc_${d.docType}`)) }]),
    { key: 'number', header: head('number', t('docNoCol')), label: t('docNoCol'), width: '100px', render: (d) => d.docNumber ?? '' },
    { key: 'lines', header: head('lines', t('linesCol')), label: t('linesCol'), width: '60px', align: 'right', render: (d) => String(d.lineCount) },
    // on a bill the money matters; on a price list, how many prices reached the ledger
    ...(kind === 'bills' ? [] : [{ key: 'costs', header: t('costsRecordedCol'), width: '110px', align: 'right' as const, render: (d: VendorDocSummary) => String(d.costsRecorded) }]),
    { key: 'unmapped', header: head('unmapped', t('unmappedCol')), label: t('unmappedCol'), width: '80px', align: 'right', render: (d) => (d.unmapped ? String(d.unmapped) : ''), cellClass: (d) => (d.unmapped ? 'warn' : undefined) },
    ...(kind === 'lists'
      ? []
      : ([
          { key: 'total', header: head('total', t('totalCol')), label: t('totalCol'), width: '100px', align: 'right', render: (d) => money(d.total) },
          { key: 'open', header: head('open', t('openCol')), label: t('openCol'), width: '100px', align: 'right', render: (d) => money(open(d)) },
          { key: 'due', header: head('due', t('billDueCol')), label: t('billDueCol'), width: '100px', render: (d) => (d.dueDate ? formatDate(d.dueDate, intlLocale, 'date') : '') },
        ] satisfies SheetColumn<VendorDocSummary>[])),
    { key: 'photo', header: t('photoCol'), width: '60px', render: (d) => (d.hasFile ? '✓' : '') },
  ];
  if (desktop) {
    return (
      <div className="desk-page">
        <header className="desk-head">
          <div>
            <h1>{title}</h1>
            <p className="muted">{hint}</p>
          </div>
          <Link to={newPath} className="btn btn-primary btn-sm">
            + {enter}
          </Link>
        </header>
        <QueryView query={docs}>
          {(rows) => (rows.length === 0 ? <Empty>{t('noResults')}</Empty> : <Sheet label={title} columns={columns} rows={sorted(rows)} rowKey={(d) => d.id} rowLabel={(d) => `${d.vendorName} ${d.docNumber ?? ''}`.trim()} minWidth="960px" stickyFirst onOpen={(d) => navigate(`/costs/${d.id}?k=${kindOf(d.docType)}`)} />)}
        </QueryView>
      </div>
    );
  }
  return (
    <div className="stack">
      <PageHead title={title} back="/" />
      <p className="muted">{hint}</p>
      <Link to={newPath} className="btn btn-primary btn-lg btn-block">
        + {enter}
      </Link>
      <QueryView query={docs}>
        {(rows) =>
          rows.length === 0 ? (
            <Empty>{t('noResults')}</Empty>
          ) : (
            <ul className="list">
              {rows.map((d) => (
                <li key={d.id}>
                  <Link to={`/costs/${d.id}?k=${kindOf(d.docType)}`} className="row">
                    <span className="row-main">
                      <span className="row-title">
                        {d.vendorName} · {t(`doc_${d.docType}`)}
                        {d.docNumber ? ` ${d.docNumber}` : ''}
                      </span>
                      <span className="row-sub">
                        {formatDate(d.docDate, intlLocale, 'date')} · {d.lineCount} {t('lines')} · {d.costsRecorded} {t('costsRecorded')}
                        {d.unmapped > 0 ? <span className="badge badge-warn">{d.unmapped} {t('unmappedLines')}</span> : null}
                        {d.hasFile ? <span className="badge">{t('photoAttached')}</span> : null}
                      </span>
                    </span>
                    {d.total !== null ? <span className="row-end-value">{formatMoney(d.total, intlLocale)}</span> : null}
                  </Link>
                </li>
              ))}
            </ul>
          )
        }
      </QueryView>
    </div>
  );
}

/** One document: its lines, the costs it recorded, the photo, and the button to attach one. */
export function VendorDocPage() {
  const { t, intlLocale } = useLocale();
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const doc = useQuery({ queryKey: qk.vendorDoc(id), queryFn: ({ signal }) => api.vendorDoc(id, signal) });
  const attach = useMutation({
    mutationFn: (file: File) => api.attachVendorDocFile(id, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.vendorDoc(id) });
      void qc.invalidateQueries({ queryKey: ['vendor-docs'] });
    },
  });
  type SavedLine = VendorDoc['lines'][number];
  const savedColumns: SheetColumn<SavedLine>[] = [
    { key: 'no', header: '#', width: '44px', align: 'right', render: (l) => l.lineNo },
    { key: 'code', header: t('vendorCode'), width: '110px', render: (l) => l.vendorCode ?? '' },
    { key: 'desc', header: t('descriptionAsPrinted'), width: '24%', render: (l) => l.description ?? '' },
    {
      key: 'item',
      header: t('productService'),
      width: '26%',
      render: (l) => (l.itemName ? l.itemName : <span className="badge badge-warn">{t('unmappedLine')}</span>),
      cellClass: (l) => (l.flags.includes('UNMAPPED') ? 'warn' : undefined),
    },
    { key: 'qty', header: t('qty'), width: '64px', align: 'right', render: (l) => (l.qty === null ? '' : formatQty(l.qty)) },
    { key: 'unit', header: t('unitCol'), width: '56px', render: (l) => l.unit ?? '' },
    { key: 'cost', header: t('unitCost'), width: '100px', align: 'right', render: (l) => (l.unitPrice === null ? '' : formatMoney(l.unitPrice, intlLocale)) },
    { key: 'total', header: t('amount'), width: '100px', align: 'right', render: (l) => (l.lineTotal === null ? '' : formatMoney(l.lineTotal, intlLocale)) },
  ];
  return (
    <QueryView query={doc}>
      {(d) => (
        <div className="stack">
          <PageHead title={`${d.vendorName} · ${t(`doc_${d.docType}`)}${d.docNumber ? ` ${d.docNumber}` : ''}`} back={kindPath(kindOf(d.docType))} />
          <dl className="facts">
            <div className="fact">
              <dt>{t('docDate')}</dt>
              <dd>{formatDate(d.docDate, intlLocale, 'date')}</dd>
            </div>
            {d.total !== null ? (
              <div className="fact">
                <dt>{t('total')}</dt>
                <dd className="money">{formatMoney(d.total, intlLocale)}</dd>
              </div>
            ) : null}
            <div className="fact">
              <dt>{t('costsRecorded')}</dt>
              <dd>{d.costsRecorded}</dd>
            </div>
          </dl>
          <Sheet label={t('docLines')} columns={savedColumns} rows={d.lines} rowKey={(l) => l.id} minWidth="760px" />
          {d.unmapped > 0 ? (
            <Link to="/items/inbox" className="btn btn-secondary btn-block">
              {t('openInbox')}
            </Link>
          ) : null}
          <section className="card stack-sm">
            <h2>{t('photo')}</h2>
            {d.hasFile ? (
              <a className="btn btn-secondary btn-block" href={`/api/vendor-documents/${d.id}/file`} target="_blank" rel="noreferrer">
                {d.fileName ?? t('photoAttached')}
              </a>
            ) : (
              <p className="muted">{t('noPhoto')}</p>
            )}
            <label className="btn btn-primary btn-lg btn-block">
              {attach.isPending ? t('sending') : t('attachPhoto')}
              <input
                type="file"
                accept="image/*,application/pdf"
                capture="environment"
                style={{ display: 'none' }}
                aria-label={t('attachPhoto')}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) attach.mutate(f);
                  e.target.value = '';
                }}
              />
            </label>
            {attach.error ? <ErrorBox error={attach.error} /> : null}
          </section>
        </div>
      )}
    </QueryView>
  );
}

interface LineDraft {
  item: Pick<Item, 'id' | 'name' | 'packUnit'> | null;
  vendorCode: string;
  description: string;
  qty: string;
  unitPrice: string;
}

type DocRow = LineDraft & { key: string };
let keySeq = 0;
const nextKey = () => `l${++keySeq}`;
const blankRow = (): DocRow => ({ key: nextKey(), item: null, vendorCode: '', description: '', qty: '', unitPrice: '' });
const isBlankLine = (l: LineDraft) => !l.item && !l.vendorCode.trim() && !l.description.trim() && !l.qty.trim() && !l.unitPrice.trim();
/** Like the invoice screen: a blank line always waits at the end, so typing in it adds a line. */
function withBlankEnd(rows: DocRow[]): DocRow[] {
  const kept = rows.filter((l, i) => !isBlankLine(l) || i === rows.length - 1);
  return kept.length && isBlankLine(kept[kept.length - 1]!) ? kept : [...kept, blankRow()];
}

export function toDocLines(lines: LineDraft[]): { lines: VendorDocLineCreate[] } | { problem: AppMessageKey } {
  if (lines.length === 0) return { problem: 'nothingToSave' };
  const out: VendorDocLineCreate[] = [];
  for (const l of lines) {
    const price = parseNumber(l.unitPrice);
    if (price === null) return { problem: 'error' };
    const qty = l.qty.trim() === '' ? null : parseNumber(l.qty);
    if (l.qty.trim() !== '' && qty === null) return { problem: 'error' };
    if (!l.item && !l.vendorCode.trim() && !l.description.trim()) return { problem: 'error' };
    out.push({
      unitPrice: price,
      ...(qty !== null ? { qty } : {}),
      ...(l.item ? { itemId: l.item.id, unit: l.item.packUnit } : {}),
      ...(l.vendorCode.trim() ? { vendorCode: l.vendorCode.trim() } : {}),
      ...(l.description.trim() ? { description: l.description.trim() } : {}),
    });
  }
  return { lines: out };
}

/** Type in a vendor invoice or price list, line by line; each line with a known item becomes a cost. */
export function NewVendorDocPage() {
  const { t, intlLocale } = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const vendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal) });
  const [vendorId, setVendorId] = useState('');
  const [params] = useSearchParams();
  const preset = VendorDocType.safeParse(params.get('type'));
  const presetType = preset.success ? preset.data : null;
  const [docType, setDocType] = useState<VendorDocType>(presetType ?? 'invoice');
  const [docNumber, setDocNumber] = useState('');
  const [docDate, setDocDate] = useState(todayIso());
  const [total, setTotal] = useState('');
  const [lines, setLines] = useState<DocRow[]>(() => withBlankEnd([]));
  const [problem, setProblem] = useState<AppMessageKey | null>(null);
  const reader = useQuery({ queryKey: qk.readerStatus, queryFn: ({ signal }) => api.readerStatus(signal) });
  const [file, setFile] = useState<File | null>(null);
  const [readNotes, setReadNotes] = useState<string[]>([]);
  const [readInfo, setReadInfo] = useState<{ duplicate: VendorDocDraft['duplicateOf']; letterhead: string | null; matched: boolean } | null>(null);
  const read = useMutation({
    mutationFn: (f: File) => api.readVendorDoc(f, vendorId),
    onSuccess: (d, f) => {
      setFile(f);
      setProblem(null);
      if (d.vendorId) setVendorId(d.vendorId);
      setDocType(d.docType);
      if (d.docNumber) setDocNumber(d.docNumber);
      if (d.docDate) setDocDate(d.docDate);
      setTotal(d.total === null ? '' : String(d.total));
      setLines(
        withBlankEnd(d.lines.map((l) => ({
          key: nextKey(),
          item: l.itemId && l.itemName ? { id: l.itemId, name: l.itemName, packUnit: UnitCode.safeParse(l.itemPackUnit).data ?? 'CS' } : null,
          vendorCode: l.vendorCode ?? '',
          description: l.description ?? '',
          qty: l.qty === null ? '' : String(l.qty),
          unitPrice: l.unitPrice === null ? '' : String(l.unitPrice),
        }))),
      );
      setReadNotes(d.notes);
      setReadInfo({ duplicate: d.duplicateOf, letterhead: d.vendorNameOnDocument, matched: d.vendorId !== null });
    },
  });
  const save = useMutation({
    mutationFn: async (body: VendorDocCreate) => {
      const d = await api.createVendorDoc(body);
      if (file) await api.attachVendorDocFile(d.id, file).catch(() => undefined); // the document is saved either way; the file can be attached again from its page
      return d;
    },
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['vendor-docs'] });
      void qc.invalidateQueries({ queryKey: ['items'] });
      navigate(`/costs/${d.id}`);
    },
  });
  const items = useQuery({ queryKey: qk.items(''), queryFn: ({ signal }) => api.items('', signal) });
  const byId = useMemo(() => new Map((items.data ?? []).map((i) => [i.id, i])), [items.data]);
  const itemOptions: PickOption[] = useMemo(
    () => (items.data ?? []).filter((i) => i.active).map((i) => ({ value: i.id, label: i.name, hint: packLabel(i), search: `${i.sku ?? ''} ${i.nameEs ?? ''}` })),
    [items.data],
  );
  const filled = lines.filter((l) => !isBlankLine(l));
  const unlinked = filled.filter((l) => !l.item).length;
  const costNow = (l: DocRow) => (l.item ? (byId.get(l.item.id)?.cost?.amount ?? null) : null);
  const changeOf = (l: DocRow): number | null => {
    const now = costNow(l);
    const price = parseNumber(l.unitPrice);
    return now === null || now === 0 || price === null ? null : (price - now) / now;
  };
  const text = (field: 'vendorCode' | 'description') => ({
    kind: 'text' as const,
    get: (l: DocRow) => l[field],
    set: (l: DocRow, v: string) => ({ ...l, [field]: v.trim() }),
  });
  const lineColumns: SheetColumn<DocRow>[] = [
    { key: 'no', header: '#', width: '44px', align: 'right', render: (l, i) => (isBlankLine(l) ? '' : i + 1) },
    { key: 'code', header: t('vendorCode'), width: '124px', render: (l) => l.vendorCode, edit: text('vendorCode') },
    { key: 'desc', header: t('descriptionAsPrinted'), render: (l) => l.description, edit: text('description') },
    {
      key: 'item',
      header: t('productService'),
      render: (l) => (l.item ? l.item.name : isBlankLine(l) ? '' : <span className="muted">{t('pickProduct')}</span>),
      edit: {
        kind: 'pick',
        options: itemOptions,
        placeholder: t('typeToSearch'),
        get: () => '',
        set: (l, v) => {
          if (v === '') return { ...l, item: null };
          const it = byId.get(v);
          return it ? { ...l, item: { id: it.id, name: it.name, packUnit: it.packUnit } } : null;
        },
      },
      cellClass: (l) => (!l.item && !isBlankLine(l) ? 'warn' : undefined),
    },
    { key: 'unit', header: t('unitCol'), width: '56px', render: (l) => l.item?.packUnit ?? '' },
    {
      key: 'qty',
      header: t('qty'),
      width: '64px',
      align: 'right',
      render: (l) => l.qty,
      edit: { kind: 'number', get: (l) => l.qty, set: (l, v) => ({ ...l, qty: v.trim() }) },
      cellClass: (l) => (l.qty.trim() !== '' && parseNumber(l.qty) === null ? 'bad' : undefined),
    },
    {
      key: 'cost',
      header: t('unitCost'),
      width: '96px',
      align: 'right',
      render: (l) => {
        const p = parseNumber(l.unitPrice);
        return p === null ? l.unitPrice : formatMoney(p, intlLocale);
      },
      edit: { kind: 'number', get: (l) => l.unitPrice, set: (l, v) => ({ ...l, unitPrice: v.trim() }) },
      cellClass: (l) => (!isBlankLine(l) && parseNumber(l.unitPrice) === null ? 'bad' : undefined),
    },
    {
      key: 'now',
      header: t('costNow'),
      hint: t('costNowHint'),
      width: '88px',
      align: 'right',
      render: (l) => {
        const n = costNow(l);
        return n === null ? '' : formatMoney(n, intlLocale);
      },
    },
    {
      key: 'change',
      header: t('costChange'),
      width: '72px',
      align: 'right',
      render: (l) => {
        const c = changeOf(l);
        return c === null ? '' : `${c > 0 ? '+' : ''}${Math.round(c * 1000) / 10}%`;
      },
      cellClass: (l) => {
        const c = changeOf(l);
        return c !== null && Math.abs(c) >= 0.1 ? 'warn' : undefined;
      },
    },
    {
      key: 'remove',
      header: '',
      label: t('remove'),
      width: '48px',
      align: 'center',
      render: (l, i) =>
        isBlankLine(l) ? null : (
          <button type="button" className="btn btn-ghost btn-sm" style={{ fontSize: 18, lineHeight: 1, padding: '0 4px', minHeight: 0 }} title={t('remove')} aria-label={`${t('remove')} ${i + 1}`} onClick={() => setLines((cur) => withBlankEnd(cur.filter((x) => x.key !== l.key)))}>
            ×
          </button>
        ),
    },
  ];
  function submit() {
    if (!vendorId) return setProblem('vendor_required');
    const r = toDocLines(filled);
    if ('problem' in r) return setProblem(r.problem);
    const parsed = VendorDocCreate.safeParse({
      vendorId,
      docType,
      docDate,
      ...(docNumber.trim() ? { docNumber: docNumber.trim() } : {}),
      ...(parseNumber(total) !== null && total.trim() !== '' ? { total: parseNumber(total)! } : {}),
      lines: r.lines,
    });
    if (!parsed.success) return setProblem('error');
    // a slip of a decimal ($1.55 for $15.50) goes straight into the costs and cannot be taken back: asked first, the
    // same 25% rule as the order day (decision 165); and lines that do not add up to the printed total (end-user test)
    const far = filled.filter((l) => {
      const c = changeOf(l);
      return c !== null && Math.abs(c) > 0.25;
    });
    const linesSum = Math.round(parsed.data.lines.reduce((t, l) => t + (l.qty ?? 1) * l.unitPrice, 0) * 100) / 100;
    const printed = parsed.data.total;
    const offTotal = docType === 'invoice' && printed !== undefined && Math.abs(linesSum - printed) > 1;
    if (far.length || offTotal) {
      const parts = [
        ...(far.length ? [t('docCostFarAsk').replace('{list}', far.slice(0, 6).map((l) => `${l.item?.name ?? l.description}: ${formatMoney(costNow(l)!, intlLocale)} → ${formatMoney(parseNumber(l.unitPrice) ?? 0, intlLocale)}`).join('\n'))] : []),
        ...(offTotal ? [t('docTotalOffAsk').replace('{lines}', formatMoney(linesSum, intlLocale)).replace('{total}', formatMoney(printed!, intlLocale))] : []),
      ];
      if (!window.confirm(`${parts.join('\n\n')}\n\n${t('docSaveAnyway')}`)) return;
    }
    setProblem(null);
    save.mutate(parsed.data);
  }
  return (
    <div className="stack">
      <PageHead title={docType === 'price_list' ? t('enterVendorPriceList') : t('enterVendorBill')} back={kindPath(presetType ? kindOf(docType) : undefined)} />
      {reader.data?.configured ? (
        <section className="card stack-sm">
          <label className="btn btn-secondary btn-lg btn-block" style={{ cursor: 'pointer' }}>
            {read.isPending ? t('reading') : t('readFromPhoto')}
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              aria-label={t('readFromPhoto')}
              style={{ display: 'none' }}
              disabled={read.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) read.mutate(f);
                e.target.value = '';
              }}
            />
          </label>
          {read.isPending ? (
            <p className="muted" role="status">
              {t('readingLong')}
            </p>
          ) : null}
          {read.error ? <ErrorBox error={read.error} /> : null}
          {readInfo ? (
            <>
              <p className="note" role="status">
                {t('readDone')}
              </p>
              {readInfo.duplicate ? (
                <p className="field-error">
                  {t('readDuplicate')} <Link to={`/costs/${readInfo.duplicate.id}`}>{readInfo.duplicate.docNumber ?? readInfo.duplicate.docDate}</Link>
                </p>
              ) : null}
              {!readInfo.matched && readInfo.letterhead ? <p className="field-error">{t('vendorUnmatched').replace('{name}', readInfo.letterhead)}</p> : null}
              {readNotes.length ? (
                <ul className="muted">
                  {readNotes.map((n, i) => (
                    <li key={i}>
                      {t('readNotes')}: {n}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}
      <div className="picker-head">
        <label className="field" style={{ flex: 1 }}>
          <span>{t('vendor')}</span>
          <select value={vendorId} onChange={(e) => setVendorId(e.target.value)} aria-label={t('vendor')}>
            <option value="">—</option>
            {(vendors.data ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('docType')}</span>
          <select value={docType} onChange={(e) => setDocType(VendorDocType.parse(e.target.value))} aria-label={t('docType')}>
            {VendorDocType.options.map((k) => (
              <option key={k} value={k}>
                {t(`doc_${k}`)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="picker-head" style={{ flexWrap: 'wrap' }}>
        <label className="field" style={{ flex: '1 1 140px' }}>
          <span>{t('docNumber')}</span>
          <input type="text" autoComplete="off" value={docNumber} onChange={(e) => setDocNumber(e.target.value)} />
        </label>
        <label className="field" style={{ flex: '1 1 150px' }}>
          <span>{t('docDate')}</span>
          <input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} required />
        </label>
        <label className="field" style={{ flex: '1 1 100px' }}>
          <span>{t('total')}</span>
          <input type="text" inputMode="decimal" autoComplete="off" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="$" />
        </label>
      </div>

      <Sheet
        label={t('docLines')}
        columns={lineColumns}
        rows={lines}
        rowKey={(l) => l.key}
        minWidth="880px"
        onRowsChange={(next) => {
          setProblem(null);
          setLines(withBlankEnd(next));
        }}
      />
      {problem ? (
        <p className="field-error" role="alert">
          {t(problem)}
        </p>
      ) : null}
      {save.error ? <ErrorBox error={save.error} /> : null}
      {filled.length > 0 ? (
        <div className="actionbar">
          <div className="total">
            <span>{t('lines')}</span>
            <strong>{filled.length}</strong>
            {unlinked > 0 ? <span className="badge badge-warn" style={{ alignSelf: 'flex-start' }}>{t('linesNotLinked').replace('{n}', String(unlinked))}</span> : null}
          </div>
          <button type="button" className="btn btn-primary btn-lg" onClick={submit} disabled={save.isPending}>
            {save.isPending ? t('sending') : t('save')}
          </button>
        </div>
      ) : null}
    </div>
  );
}
